import React, {useRef, useState} from 'react';
import {Navigate, useNavigate, useParams} from "react-router-dom";
import {useQuery} from "@apollo/client";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {Box, Button as JoyButton, Chip, CircularProgress, Typography} from "@mui/joy";
import {ArrowBackRounded} from "@mui-symbols-material/w300";
import {Error} from "@dreebit-registry/vsm-microservice-components";
import {ContaminationDeclaration} from "../../graphql/queries/contaminationDeclaration";
import {AssetActiveLocation} from "../../graphql/queries/assetActiveLocation";
import {sanitizeErrorForDisplay} from "../../utils/resolveErrorMessage";
import {useContaminationDeclarationAccess} from "../../hooks/useContaminationDeclarationAccess";
import ContaminationDeclarationForm from "../../components/ContaminationDeclarationForm";
import {ContaminationDeclarationData} from "../../components/ContaminationDeclarationForm/types";

interface LocationLike {
    id?: string | null;
}

// The asset detail page is routed by assetLocationId. assetLocationList only
// returns the locations the user can access, so the first one is reachable.
const pickAssetLocationId = (locations?: Array<LocationLike | null> | null): string | undefined =>
    (locations ?? []).find((l) => !!l?.id)?.id ?? undefined;

/**
 * Editor route (/contaminationDeclarations/:declarationId). The form is the
 * whole page: header with the asset snapshot meta, sections below, sticky
 * save footer. Reached from the removal wizard's follow-up prompt, the event
 * drawer, the event-history status column and the asset's DoC section.
 */
const ContaminationDeclarationDetailScreen = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {declarationId} = useParams<{declarationId: string}>();
    const {canUpdate} = useContaminationDeclarationAccess();
    const dirtyRef = useRef(false);
    // State (not a ref) so the form re-renders once the sidebar node exists and can portal into it.
    const [sidebarEl, setSidebarEl] = useState<HTMLDivElement | null>(null);

    const {data, loading, error} = useQuery(ContaminationDeclaration, {
        variables: {id: declarationId},
        skip: !declarationId,
        fetchPolicy: "cache-and-network",
    });
    const declaration: ContaminationDeclarationData | undefined = data?.contaminationDeclaration ?? undefined;

    const locationQuery = useQuery(AssetActiveLocation, {
        variables: {assetId: declaration?.assetId},
        skip: !declaration?.assetId,
    });
    const assetLocationId = pickAssetLocationId(locationQuery.data?.asset?.assetLocationList?.assetLocations);

    const handleBack = () => {
        if (dirtyRef.current && !window.confirm(t('You have unsaved changes. Leave without saving?'))) return;
        // Reopen the removal event the declaration belongs to.
        const eventParam = declaration?.installedBaseEventId ? `?event=${encodeURIComponent(declaration.installedBaseEventId)}` : '';
        navigate(assetLocationId ? `/assets/${assetLocationId}/events${eventParam}` : '/assets');
    };

    const backButton = (
        <JoyButton
            variant="plain"
            size="sm"
            startDecorator={<ArrowBackRounded fontSize="small"/>}
            onClick={handleBack}
            data-testid="doc-back"
        >
            {t('Back to asset')}
        </JoyButton>
    );

    if (!declarationId) return <Navigate to="/dashboard" replace/>;

    if (loading && !declaration) {
        return (
            <Box sx={{flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <CircularProgress variant="soft"/>
            </Box>
        );
    }

    if (error && !declaration) {
        return (
            <Box sx={{flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px', p: '8px 12px'}}>
                <Box sx={{flexShrink: 0}}>{backButton}</Box>
                <Error errorObject={sanitizeErrorForDisplay(error, t)}/>
            </Box>
        );
    }

    if (!declaration) return <Navigate to="/dashboard" replace/>;

    const metaParts = [
        declaration.productModelName,
        declaration.serialNumber ? `${t('Serial No.')} ${declaration.serialNumber}` : null,
        declaration.insert ? `${t('Removal event of')} ${moment(declaration.insert).format('L')}` : null,
    ].filter(Boolean);

    // Title + meta live inside the form's centered column so they align with the sections.
    const header = (
        <Box sx={{pb: '20px', pt: '8px', minWidth: 0, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap'}}>
            <Box sx={{flex: 1, minWidth: 0}}>
                <Typography level="titleLarge" noWrap>
                    {t('Declaration of Contamination')}
                </Typography>
                <Typography level="caption" sx={{color: '#666666'}} noWrap title={metaParts.join(' · ')}>
                    {metaParts.join(' · ')}
                </Typography>
            </Box>
            <Box sx={{display: 'flex', gap: '6px', alignItems: 'center'}}>
                {!canUpdate && (
                    <Chip variant="soft" color="neutral" size="sm" data-testid="doc-readonly">
                        {t('Read-only')}
                    </Chip>
                )}
                <Chip variant="outlined" color="neutral" size="sm" title={t('Template')}>
                    {`${declaration.templateKey ?? 'DoC'} v${declaration.templateVersion ?? '?'}`}
                </Chip>
            </Box>
        </Box>
    );

    return (
        <Box
            data-testid="contamination-declaration-detail"
            sx={{flex: 1, minWidth: 0, minHeight: 0, display: 'flex'}}
        >
            <Box sx={{flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column'}}>
                <Box sx={{flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px', p: '8px 12px 0'}}>
                    {backButton}
                    <Box sx={{flex: 1}}/>
                </Box>

                {/* Only the form column scrolls; the sidebar beside it stays put. */}
                <Box sx={{flex: 1, minHeight: 0, overflow: 'auto', position: 'relative'}}>
                    <ContaminationDeclarationForm
                        declaration={declaration}
                        canUpdate={canUpdate}
                        header={header}
                        sidebarContainer={sidebarEl}
                        onDirtyChange={(dirty) => { dirtyRef.current = dirty; }}
                    />
                </Box>
            </Box>

            {/* Full-height white sidebar (prototype), from the app header down; the
                form portals its progress panel in here. Hidden below `lg`. */}
            <Box
                ref={setSidebarEl}
                data-testid="doc-sidebar"
                sx={{
                    width: 300, flexShrink: 0, minHeight: 0, overflowY: 'auto',
                    display: {xs: 'none', lg: 'block'},
                    backgroundColor: '#fff', borderLeft: '1px solid #e0e0e0',
                }}
            />
        </Box>
    );
};

export default ContaminationDeclarationDetailScreen;
